import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';

/** Kroll group (NH) membership as stamped on orders/baskets or returned live on the patient. */
export interface KrollGroupInfo {
  groupId?: number;
  groupName: string;
  stampedAt?: string;
}

interface GroupPatientChipProps {
  /** Group info, or null/undefined when the patient has none — renders nothing. */
  group?: KrollGroupInfo | null;
  /** Cap the chip width when showLabel is on (default 130px; full name in tooltip). */
  maxWidth?: number;
  /**
   * Render the NH group name inline. Default TRUE (GP-1311): a bare house icon
   * told the user nothing about WHICH home, so every call site — queue grids
   * included — gets the labelled chip, capped at `maxWidth` with the full name
   * still in the tooltip. Pass `showLabel={false}` only where horizontal space
   * genuinely cannot fit a label.
   */
  showLabel?: boolean;
}

/**
 * Nursing Home (NH) marker shown next to a patient's name (GP-521 §1).
 *
 * Renders the NH group NAME in a compact chip (GP-1311 — the icon-only default
 * left queues showing an anonymous house), truncated to `maxWidth` with the full
 * name in the tooltip. Pass `showLabel={false}` for the icon-only variant.
 * Renders nothing when the patient has no NH group.
 *
 * UNGATED — NH identification is universal, so this shows wherever a patient
 * renders for any NH-flagged patient. The per-pharmacy "Track Groups" toggle now
 * governs ONLY the no-buffer follow-up-date math, not this marker (that gating is
 * why NH patients were invisible in the queues before).
 */
export const GroupPatientChip: React.FC<GroupPatientChipProps> = ({
  group,
  maxWidth = 130,
  showLabel = true,
}) => {
  if (!group?.groupName) {
    return null;
  }

  const title = `Nursing home: ${group.groupName}`;

  if (!showLabel) {
    // Opt-in icon-only marker — name lives in the tooltip.
    return (
      <Tooltip title={title} arrow>
        <HomeIcon
          color="secondary"
          aria-label={`Nursing home patient: ${group.groupName}`}
          sx={{ fontSize: 18, verticalAlign: 'middle', cursor: 'default' }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={title} arrow>
      <Chip
        // span so the chip is valid inside Typography (<p>) call sites
        component="span"
        icon={<HomeIcon sx={{ fontSize: 14 }} />}
        label={group.groupName}
        size="small"
        color="secondary"
        variant="outlined"
        sx={{
          maxWidth,
          height: 20,
          fontSize: '0.7rem',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Tooltip>
  );
};
